import React from 'react'
import clsx from 'clsx'
import { uiSettings } from 'etc/settings'
import { makeStyles } from '@material-ui/core/styles'
import Drawer from '@material-ui/core/Drawer'
import Hidden from '@material-ui/core/Hidden'
import ProfilePanel from 'components/ProfilePanel'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Branding from 'components/navigation/branding'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReduxState } from 'types/reducers'
import { Module } from 'enums/Module'
import { CompanyModuleAccessDto } from 'data/dtos/CompanyModuleAccessDto'
import SidebarProfilePanel from 'components/SidebarProfilePanel'
import { Grid, IconButton, List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import MenuIcon from '@material-ui/icons/Menu'
import SettingsIcon from '@material-ui/icons/Settings'
import { toggleMobileMenu, toggleSideDrawer } from 'reducers/stateReducer'
import { PUBLIC_SANS_FONT_FAMILY, SIDEBAR_MENU_FONT_WEIGHT } from 'etc/appFont'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: uiSettings.drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.type === 'dark' ? '#1e293b' : '#006599',
    color: '#ffffff',
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontWeight: SIDEBAR_MENU_FONT_WEIGHT,
    '& .MuiListItemIcon-root': {
      minWidth: 50,
      color: '#ffffff',
    },
    '& .MuiListItemText-primary': {
      color: 'rgba(255, 255, 255, 1)',
      fontFamily: PUBLIC_SANS_FONT_FAMILY,
      fontWeight: SIDEBAR_MENU_FONT_WEIGHT,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0px',
    },
    '& .MuiListItemText-secondary': {
      color: 'rgba(255,255,255,1)',
      fontFamily: PUBLIC_SANS_FONT_FAMILY,
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0px',
    },
  },

  whiteIcon: {
    color: "#fff",
  },

  drawerOpen: {
    width: uiSettings.drawerWidth,
    zIndex: 1202,
    transition: theme.transitions.create(['width', 'zIndex'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  drawerClose: {
    transition: theme.transitions.create(['width', 'zIndex'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    zIndex: 1210,
    overflowX: 'hidden',
    width: uiSettings.drawerWidthClosed,
    [theme.breakpoints.up('sm')]: {
      width: uiSettings.drawerWidthClosed,
    },
  },

  toolbar: (props: any) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    gap: '10px',
    borderBottom: '1px solid rgba(229, 231, 235, 0.3)',
    height: props.bigScreen
      ? `${uiSettings.header.appBarHeight}px`
      : `${uiSettings.header.appBarHeightMobile}px`,
    boxSizing: 'border-box',
    flexShrink: 0,
  }),

  column: {
    flexDirection: "column",
    alignItems: "flex-start",
  },

  logo: {
    width: 170,
    height: 30,
    objectFit: 'contain',
    transform: 'rotate(0deg)',
    transition: theme.transitions.create(['transform'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  ml8: {
    marginLeft: 8,
  },

  sideMenuWrapper: {
    overflowX: 'hidden',
    overflowY: 'hidden',
    maxHeight: `calc(100% - ${uiSettings.header.appBarHeight}px)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: `calc(100% - ${uiSettings.header.appBarHeight}px)`,

    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.type === 'light' ? '#f1f1f1' : theme.palette.grey[800],
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.type === 'light' ? '#dadada' : '#b1b1b1',
      '&:hover': {
        background: theme.palette.type === 'light' ? '#b1b1b1' : '#dadada',
      },
    },
  },

  settingsSection: {
    borderTop: '1px solid rgba(229, 231, 235, 0.3)',
    padding: '4px 0',
    flexShrink: 0,
  },

  sideMenuContainer: {
    margin: '0px auto',
    width: '100%',
    flex: 1,
    // maxHeight: '89dvh', //'calc(90% - 65px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    transition: theme.transitions.create(['width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },

    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255,255,255,0.35)',
      borderRadius: '10px',
    },

    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(255,255,255,0.6)',
    },
  },

  sideMenuContainerOpen: {
    width: '100%',
  },

  profileWrapper: {
    width: '100%',
  },
}))

interface Props {
  /** @deprecated */
  expanded?: boolean
  /** @deprecated */
  mobileMenuOpen?: boolean
  /** @deprecated */
  mobileMenuToggle?: () => void
  /** @deprecated */
  toggleExpanded?: () => void
}

const SideNavigation: React.FC<Props> = ({ children }) => {
  const bigScreen = useMediaQuery('(min-width:960px)')
  const classes = useStyles({ bigScreen })
  const smallScreen = useMediaQuery('(max-width:600px)')
  const expanded = useSelector((state: IRootReduxState) => state.app.state.sideMenuExpanded)
  const mobileMenuOpen = useSelector((state: IRootReduxState) => state.app.state.mobileMenuOpen)
  const moduleAccess = useSelector((state: IRootReduxState) => state.app.user.access)

  // Show the Settings gear only when the user can access at least one settings tab
  const settingsModules = [
    Module.CompanySettings, Module.OrganizationChart,
    Module.CompanyProfile,  Module.UserProfile,
    Module.SecuritySettings, Module.SystemPreferences,
    Module.Notifications,   Module.ActivityLog,
    Module.RolesPermissions,
  ]
  const hasSettingsAccess = settingsModules.some(m =>
    (moduleAccess?.find(i => (i as CompanyModuleAccessDto).Module === m) as CompanyModuleAccessDto)?.AccessAllowed
  )
  const dispatch = useDispatch()
  const intl = useIntl()
  const history = useHistory()
  const openMenuAria = intl.formatMessage({
    id: 'navigation.openDrawer',
    defaultMessage: 'Open menu',
  })
  const toggleSidebarAria = intl.formatMessage({
    id: 'navigation.toggleSidebar',
    defaultMessage: 'Toggle sidebar',
  })
  const settingsLabel = intl.formatMessage({
    id: 'menu.settings',
    defaultMessage: 'Settings',
  })

  return (
    <div>
      <Hidden mdUp implementation="css">
        <Drawer
          variant="temporary"
          classes={{
            paper: classes.drawer,
          }}
          anchor="left"
          open={mobileMenuOpen}
          onClose={() => dispatch(toggleMobileMenu())}
          ModalProps={{ keepMounted: true }}
        >
          <div className={classes.toolbar}>
            <Branding width={153} variant="sidebar"/>
            <Hidden mdUp implementation="css">
              <IconButton aria-label={openMenuAria} onClick={() => dispatch(toggleMobileMenu())}>
                <MenuIcon className={classes.whiteIcon} />
              </IconButton>
            </Hidden>
          </div>

          <div className={classes.sideMenuWrapper}>
            <div className={clsx(classes.sideMenuContainer, { [classes.sideMenuContainerOpen]: expanded })}>
              {children}
              {hasSettingsAccess && (
                <div className={classes.settingsSection}>
                  <List>
                    <ListItem button onClick={() => { history.push('/settings/company'); dispatch(toggleMobileMenu()); }}>
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary={settingsLabel} />
                    </ListItem>
                  </List>
                </div>
              )}
              <Hidden implementation="css" smUp>
                <ProfilePanel mobileScreen={true} />
              </Hidden>
            </div>
          </div>
        </Drawer>
      </Hidden>

      <Hidden smDown implementation="css">
        <Drawer
          variant="permanent"
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: expanded,
            [classes.drawerClose]: !expanded,
          })}
          classes={{
            paper: clsx(
              classes.drawer,
              expanded ? classes.drawerOpen : classes.drawerClose
            ),
          }}
        >
          <div className={clsx(classes.toolbar, { [classes.column]: !expanded, })}>
            <Branding width={153} max-height={35} />
            <Grid item>
              <Hidden smDown implementation="css">
                <IconButton
                  className={clsx({ [classes.ml8]: expanded, "p-1": !expanded })}
                  aria-label={toggleSidebarAria}
                  onClick={() => dispatch(toggleSideDrawer())}
                >
                  <MenuIcon className={classes.whiteIcon} />
                </IconButton>
              </Hidden>
              <Hidden mdUp implementation="css">
                <IconButton aria-label={openMenuAria} onClick={() => dispatch(toggleMobileMenu())}>
                  <MenuIcon className={classes.whiteIcon} />
                </IconButton>
              </Hidden>
            </Grid>
          </div>

          <div className={classes.sideMenuWrapper}>
            <div className={clsx(classes.sideMenuContainer, { [classes.sideMenuContainerOpen]: expanded })}>
              {children}
            </div>
            {hasSettingsAccess && (
              <div className={classes.settingsSection}>
                <List>
                  <ListItem button onClick={() => history.push('/settings/company')}>
                    <ListItemIcon>
                      <SettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary={settingsLabel} />
                  </ListItem>
                </List>
              </div>
            )}
          </div>
        </Drawer>
      </Hidden>
    </div>
  )
}

export default SideNavigation